import { RunParameter } from "../../../../api";
import { Parameters } from "../Parameters";
import { IParameterRenderer, IParamView, ParameterOwnerModel, RenderParamLabSettingsProps } from "../ParameterRendererAbstracts";
import { CaseRunCalculateModel, CaseRunClient, CaseRunCreateModel, CaseRunCreateModelBase, DeconvMainCaseRunCreateModel, DeconvMainParameters, LabClient, MaxLrCaseRunCreateModelBase, ParameterTypes, RunParameterModel } from "@app/api2/api";
import { ICreateRenderer } from "../CaseRunCreate";
import { ReviewMaxLr } from "../ReviewMaxLr";
import { Form, FormField } from "@/components/bp/bp-ui";
import { Field } from "formik";
import { FormState, InformationDeconvMain } from "../InformationDeconvMain";
import { MarkerSpecificSettingsEditor } from "../MarkerSpecificSettingsEditor";
import { RareAllelesFreqSelect } from "@common/RareAllelesFreqSelect";

export class DeconvMainHandler implements IParameterRenderer, ICreateRenderer<DeconvMainCaseRunCreateModel, FormState> {

    caseRunClient = new CaseRunClient();
    labClient = new LabClient();

    hasOwnRunList?: () => boolean = () => false;

    save(props: { labId: number, caseId: number, model: CaseRunCreateModelBase }) {
        return this.caseRunClient.createDeconvMain(props.labId, props.caseId, props.model as DeconvMainCaseRunCreateModel)
    }

    createBlankModel(props: { labId: number, caseId: number, runType: string, evidenceId?: number, ednaTestId?: number }): Promise<DeconvMainCaseRunCreateModel> {
        return this.caseRunClient.new(props.labId, props.caseId, props.runType, props.evidenceId, props.ednaTestId ?? null) as Promise<DeconvMainCaseRunCreateModel>
    }

    renderReview(model: CaseRunCreateModelBase, onComplete: (values: any) => void, router: any) {
        return <ReviewMaxLr model={model as MaxLrCaseRunCreateModelBase} onComplete={onComplete} router={router} />
    }

    renderInformation(model: CaseRunCreateModelBase, onComplete: (values: any) => void, router: any, rangeOfContributors: boolean) {
        return <InformationDeconvMain model={model as DeconvMainCaseRunCreateModel} onComplete={onComplete} />
    }

    runType: string = "deconv_main";

    canHandle(runType: string): boolean {
        return runType == this.runType;
    }

    applyInformationValuesToModel = (model: DeconvMainCaseRunCreateModel, values: FormState) => {
        model.description = values.description;
        model.hpUnknowns = parseInt(values.unknowns) || 0;
        model.hp = values.contributors;
        model.evidence = values.evidence;
        model.populationId = values.populationId;
        model.numberOfContributors = values.numberOfContributors;

        // set default value for outNoc
        let outNoc = model.parameters.find(x => x.parameter == "noc");
        if (outNoc != null)
            outNoc.value = values.numberOfContributors;


        let isAIC = model.parameters.find(x => x.parameter == "isAIC");
        if (isAIC != null)
            isAIC.value = values.isAIC ? "1" : "0";

        let hpUnknowns = model.parameters.find(x => x.parameter == "hpUnknowns");
        if (hpUnknowns != null)
            hpUnknowns.value = model.hpUnknowns.toString();

        // params.noc = values.numberOfContributors;
        // params.hpUnknowns = parseInt(values.unknowns)
        // params.isAIC = values.isAIC;

        // objectToParameters(params, model.parameters)


        return model;
    };

    propsComplete = (model: DeconvMainCaseRunCreateModel | any): void => {
        model.parameters = model.parameters.sort((a, b) => a.order < b.order ? -1 : 1)
    };

    renderParameterEditor(model: CaseRunCreateModelBase | CaseRunCalculateModel, p: RunParameter, values, onChange, errors, setFieldValue, parameters: Parameters): JSX.Element {
        return p.parameter == "detectionThreshold" ? (<FormField error={!!errors[p.parameter]}>
            <Field name={p.parameter}
                disabled={false} onChange={async (e, v) => { onChange(p, e.target.value); await this.calcDelta(model.labId, e.target.value, parameters); }} />
        </FormField>) : p.parameter == "detectionThresholds" ? (
            <MarkerSpecificSettingsEditor
                readOnly={false}
                onChange={e => { onChange(p, e) }}
                onMarkerValueChange={async (marker, value) => { await this.calcDeltaForMarker(model.labId, value, parameters, marker); }}
                // onMarkersValueChange={async (markers, value) => { await this.calcDeltaForDye(model.labId, value, parameters, markers); }}
                evidenceId={(model as MaxLrCaseRunCreateModelBase).evidence.find(x => x.selected)?.id}
                caseId={model.efmCaseId}
                name={p.parameter}
                labId={model.labId}
                value={values[p.parameter]}
                defaultValue={values.detectionThreshold} />
        ) : p.parameter == "dropInProbabilities" ? (
            <MarkerSpecificSettingsEditor
                readOnly={false}
                onChange={e => { onChange(p, e) }}
                evidenceId={(model as MaxLrCaseRunCreateModelBase).evidence.find(x => x.selected)?.id}
                caseId={model.efmCaseId}
                name={p.parameter}
                labId={model.labId}
                value={values[p.parameter]}
                defaultValue={values.dropInProbability} />
        ) : p.parameter == "dropInHyperParams" ? (<MarkerSpecificSettingsEditor
            readOnly={false}
            onChange={e => { onChange(p, e) }}
            evidenceId={(model as MaxLrCaseRunCreateModelBase).evidence.find(x => x.selected)?.id}
            caseId={model.efmCaseId}
            name={p.parameter}
            labId={model.labId}
            value={values[p.parameter]}
            defaultValue={values.dropInHyperParam} />) : (
            <RareAllelesFreqSelect name={p.parameter} value={values[p.parameter]} onChange={(ev, val) => { onChange(p, val) }} />
        )
    }

    calcDelta(labId: number, value: any, parameters: Parameters) {
        return this.labClient.calculateDropIn(labId, value).then(async (res) => {
            await parameters.form.current.setFieldValue("dropInHyperParam", res.lambda);
            await parameters.form.current.setFieldValue("dropInProbability", res.dropInProbability);
        }, reason=>{
            console.error("Error calculating drop-in:", reason);
            
        }).catch(async (e) => {
            console.error("Error calculating drop-in:", e);
        });

        // try {
        //     let res = await this.labClient.calculateDropIn(labId, value);

        //     await parameters.form.current.setFieldValue("dropInHyperParam", res.lambda);
        //     await parameters.form.current.setFieldValue("dropInProbability", res.dropInProbability);
        // }
        // catch (e) {
        //     console.error("Error calculating drop-in:", e);
        // }
    }

    async calcDeltaForMarker(labId: number, value: any, parameters: Parameters, marker: string) {
        let res = await this.labClient.calculateDropIn(labId, value);

        var dropInProps = JSON.parse(parameters.form.current.values.dropInProbabilities)
        var dropInHyps = JSON.parse(parameters.form.current.values.dropInHyperParams)

        dropInProps[marker] = res.dropInProbability
        dropInHyps[marker] = res.lambda

        await parameters.form.current.setFieldValue("dropInHyperParams", JSON.stringify(dropInHyps));
        await parameters.form.current.setFieldValue("dropInProbabilities", JSON.stringify(dropInProps));
    }

    canRenderParameter(parameterName: string): boolean {
        return parameterName == "detectionThreshold"
            || parameterName == "detectionThresholds"
            || parameterName == "dropInHyperParams"
            || parameterName == "dropInProbabilities"
            || parameterName == "rareAlleleFreqType";
    }

    renderParameterView(props: { model: ParameterOwnerModel, p: IParamView }): JSX.Element {
        return props.p.parameterType == ParameterTypes.Json ? <span>
            <MarkerSpecificSettingsEditor
                readOnly={true}
                name={props.p.parameter}
                value={props.p.value}
                labId={props.model.labId}
                caseId={props.model.efmCaseId}
                evidenceId={props.model.evidence.find(x => true)?.id} />

        </span> : props.p.parameter == "rareAlleleFreqType" ?
            <RareAllelesFreqSelect readOnly name={props.p.parameter} value={props.p.value as any} /> : <span>{props.p.value}</span>
    }

    renderParameterLabSettings?(p: RenderParamLabSettingsProps): JSX.Element {
        return p.parameter == "rareAlleleFreqType" ? <RareAllelesFreqSelect readOnly={p.readOnly} name={p.parameter} value={p.value as any} onChange={(ev, a) => { p.onChange(a as any) }} /> : null;
    }

}

// function parametersToObject<T>(parameters: RunParameterModel[], obj: T) {
//     for (let p of parameters) {
//         obj[p.parameter] = p.value;
//     }

//     return obj;
// }

// function objectToParameters<T>(obj: T, parameters: RunParameterModel[]) {
//     for (let key of Object.keys(obj)) {
//         let param = parameters.find(x => x.parameter == key);
//         if (param)
//             param.value = obj[key]
//     }

//     return parameters;
// }

